import { Box } from "@chakra-ui/react";

/**
 * CountTag — small numeric badge used in card headers, tab labels, etc.
 * design ref: styles.css:569
 */

export interface CountTagProps {
  count: number | string;
  variant?: "neutral" | "azurite" | "outline";
}

/* design ref: styles.css:569 — base count-tag (no separate azurite variant in
   styles.css; azurite mirrors the tone used by .count-pill active state at
   styles.css:62 — azurite-100 bg / azurite-700 text). The `outline` variant is
   transparent with a 1px currentColor hairline and inherits its parent's text
   colour, so it reads as a quiet count on a coloured/tinted trigger. */
const VARIANT: Record<
  NonNullable<CountTagProps["variant"]>,
  { bg: string; color: string; border: string }
> = {
  neutral: { bg: "neutral.200", color: "neutral.700", border: "none" },
  azurite: { bg: "p.100", color: "p.700", border: "none" },
  outline: { bg: "transparent", color: "inherit", border: "1px solid" },
};

const CountTag = ({ count, variant = "neutral" }: CountTagProps) => {
  const palette = VARIANT[variant];
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      minW="22px"
      h="20px"
      px="1xs" // 6px
      bg={palette.bg}
      color={palette.color}
      border={palette.border}
      borderColor="currentColor"
      // 11px is an inline literal — sits between fontSizes.2xs (10) and xs (12)
      // and is not a theme token. Per styles.css:575.
      fontSize="11px"
      fontWeight={600}
      lineHeight="1"
      borderRadius="component.md" // 6px
    >
      {count}
    </Box>
  );
};

export default CountTag;
